import { WabunkaExperienceEventListGroupByDailyResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event';

import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { useFetchFromPublicWabunkaApiKey, useRuntimeConfigKey, type UseFetchFromPublicWabunkaApi, type UseRuntimeConfig } from '@/injectionKeys';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { buildWabunkaExperienceEventDictViewModelFromDaily, type WabunkaExperienceEventDictViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
/**
 * Wabunka体験情報の取得に使用する Composable
 * Page でのみ使用可能
 */

export const useListExperienceEventsByDate = (idOrSlug: Ref<string>, query: { startDateFrom: Ref<string>; startDateTo: Ref<string> }, options?: { accumulate?: boolean; customKey?: string }) => {
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const onlyWithoutTimeSlot = !runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED;
  const onlyWithTimeSlot = runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED;
  const { selectedCurrency } = useCurrency();

  const useFetchFromPublicWabunkaApi = inject(useFetchFromPublicWabunkaApiKey) as UseFetchFromPublicWabunkaApi;
  const path = computed(() => `/experiences/${encodeURIComponent(idOrSlug.value)}/experienceEvents/byDate`);
  const queryRef = computed(() => ({
    includes: 'experienceEventInterpreterOptions,experienceEventExperienceFees,effectiveReservationType',
    experienceEventStartDateFrom: query.startDateFrom.value,
    experienceEventStartDateTo: query.startDateTo.value,
    sort: 'startAt',
    perPage: '0',
    onlyWithoutTimeSlot,
    onlyWithTimeSlot,
    ...(selectedCurrency.value !== 'JPY' ? { currency: selectedCurrency.value } : {}),
  }));

  const { data, status } = useFetchFromPublicWabunkaApi({
    path,
    query: queryRef,
    options: { server: false, watch: [idOrSlug, query.startDateFrom, query.startDateTo], customKey: options?.customKey },
    converter: WabunkaExperienceEventListGroupByDailyResponse.fromJSON,
  });

  const accumulatedDict = ref<WabunkaExperienceEventDictViewModel>({});

  // 体験IDが変わったら混入防止のためキャッシュをクリア
  watch(idOrSlug, () => {
    accumulatedDict.value = {};
  });

  watch(
    data,
    (newData) => {
      const dailyList = newData?.dailyWabunkaExperienceEvents;
      if (!options?.accumulate) {
        accumulatedDict.value = dailyList?.length ? buildWabunkaExperienceEventDictViewModelFromDaily(dailyList) : {};
        return;
      }

      // 累積モード: 新しいデータがなければ更新しない
      if (!dailyList?.length) {
        return;
      }

      accumulatedDict.value = { ...accumulatedDict.value, ...buildWabunkaExperienceEventDictViewModelFromDaily(dailyList) };
    },
    { immediate: true },
  );

  const experienceEventDict = accumulatedDict;

  const experienceEvents = computed(() =>
    Object.values(experienceEventDict.value)
      .flat()
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime()),
  );

  return { experienceEvents, experienceEventDict, ...useAsyncDataRequestStatus(status) };
};
